import { useState } from 'react';
import { Link } from 'react-router-dom';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  ShieldCheckIcon,
  HeartIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  LightBulbIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------ */
/*  Quiz data                                                         */
/* ------------------------------------------------------------------ */

interface QuizOption {
  label: string;
  points: number;
  isUnknown?: boolean;
}

interface QuizQuestion {
  id: string;
  pillar: 'safety' | 'wellness' | 'connection';
  question: string;
  options: QuizOption[];
}

// Question order: start with something the user can answer confidently,
// move falls to position 3 so momentum and trust are built first.
const QUESTIONS: QuizQuestion[] = [
  // Safety, start with the concrete, observable question
  {
    id: 'home_modifications',
    pillar: 'safety',
    question:
      'Are there grab bars in their bathroom and handrails on all stairs?',
    options: [
      { label: 'Yes, fully set up', points: 3 },
      { label: 'Some, but not everywhere needed', points: 2 },
      { label: 'No modifications yet', points: 1 },
      { label: "I haven't checked", points: 1, isUnknown: true },
    ],
  },
  {
    id: 'emergency_access',
    pillar: 'safety',
    question:
      'Could your parent easily reach help in an emergency from anywhere in their home?',
    options: [
      { label: 'Yes, they have a plan and a way to call', points: 3 },
      { label: 'Probably, but it depends on where they are', points: 2 },
      { label: 'No reliable way right now', points: 1 },
      { label: "I'm not sure", points: 1, isUnknown: true },
    ],
  },
  // Falls moved to position 3, emotionally loaded, asked after trust is built
  {
    id: 'falls',
    pillar: 'safety',
    question: 'Has your parent had a fall or near-fall in the past year?',
    options: [
      { label: 'No falls at all', points: 3 },
      { label: 'One near-fall but no injury', points: 2 },
      { label: 'Yes, at least one fall', points: 1 },
      { label: "I'm not sure", points: 1, isUnknown: true },
    ],
  },
  // Wellness
  {
    id: 'medications',
    pillar: 'wellness',
    question: 'How many daily medications does your parent take?',
    options: [
      { label: 'None', points: 3 },
      { label: '1 to 3', points: 2 },
      { label: '4 or more', points: 1 },
      { label: "I don't know", points: 1, isUnknown: true },
    ],
  },
  {
    id: 'routine',
    pillar: 'wellness',
    question:
      "How would you describe your parent's daily routine?",
    options: [
      { label: 'Very consistent day to day', points: 3 },
      { label: 'Somewhat consistent', points: 2 },
      { label: 'Unpredictable or changing', points: 1 },
      { label: "I don't really know", points: 1, isUnknown: true },
    ],
  },
  // Connection
  {
    id: 'social',
    pillar: 'connection',
    question:
      'How often does your parent interact with family or friends (in person, phone, or video)?',
    options: [
      { label: 'Daily', points: 3 },
      { label: 'A few times a week', points: 2 },
      { label: 'Weekly or less', points: 1 },
      { label: 'Rarely', points: 0 },
    ],
  },
  {
    id: 'awareness',
    pillar: 'connection',
    question:
      "If something changed with your parent's health or routine, how quickly would you know?",
    options: [
      { label: 'Same day', points: 3 },
      { label: 'Within a few days', points: 2 },
      { label: 'It might take a week or more', points: 1 },
      { label: "I honestly wouldn't know", points: 1, isUnknown: true },
    ],
  },
];

const MAX_SCORE = QUESTIONS.length * 3; // 21

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function pillarScore(
  answers: Record<string, number>,
  pillar: 'safety' | 'wellness' | 'connection',
) {
  const qs = QUESTIONS.filter((q) => q.pillar === pillar);
  const max = qs.length * 3;
  const earned = qs.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0);
  return { earned, max, pct: max > 0 ? Math.round((earned / max) * 100) : 0 };
}

function overallBand(total: number) {
  if (total >= 18)
    return {
      label: 'Strong Foundation',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      message:
        'Your parent has a solid safety foundation in place. The recommendations below can help you keep it that way.',
    };
  if (total >= 12)
    return {
      label: 'Some Gaps to Address',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      message:
        'A few areas could use your attention. The good news: most of these are straightforward to improve once you know where to look.',
    };
  if (total >= 6)
    return {
      label: 'Room for Improvement',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      message:
        'Several areas need attention, but now you have a clear picture. Start with the first recommendation below and build from there.',
    };
  return {
    label: 'Building a Stronger Foundation',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    message:
      'Your family\'s safety setup has real gaps you can start closing today. The steps below will help you focus on what matters most.',
  };
}

const PILLAR_META = {
  safety: {
    label: 'Home Safety',
    Icon: ShieldCheckIcon,
    color: 'text-blue-600',
    barColor: 'bg-blue-500',
    bgColor: 'bg-blue-50',
  },
  wellness: {
    label: 'Daily Wellness',
    Icon: HeartIcon,
    color: 'text-emerald-600',
    barColor: 'bg-emerald-500',
    bgColor: 'bg-emerald-50',
  },
  connection: {
    label: 'Family Connection',
    Icon: UserGroupIcon,
    color: 'text-purple-600',
    barColor: 'bg-purple-500',
    bgColor: 'bg-purple-50',
  },
} as const;

const QUIZ_URL = 'https://seniorthrive.com/home-safety-quiz';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type Phase = 'intro' | 'persona' | 'quiz' | 'email' | 'results';
type Persona = 'self' | 'family' | 'pro';

/**
 * Rewrite question/recommendation text for the selected persona.
 * Default quiz text uses "your parent" (family persona).
 */
function personalize(text: string, persona: Persona): string {
  if (persona === 'family') return text;
  if (persona === 'self') {
    return text
      .replace(/\bHas your parent\b/g, 'Have you')
      .replace(/\bDoes your parent\b/g, 'Do you')
      .replace(/\byour parent's\b/gi, 'your')
      .replace(/\byour parent\b/gi, 'you')
      .replace(/\btheir home\b/g, 'your home')
      .replace(/\btheir bathroom\b/g, 'your bathroom')
      .replace(/\btheir health\b/g, 'your health')
      .replace(/\btheir daily routine\b/g, 'your daily routine')
      .replace(/\btheir\b/g, 'your')
      .replace(/\byour family\b/gi, 'the people close to you')
      .replace(/\bfor your family\b/gi, 'for you');
  }
  // pro
  return text
    .replace(/\byour parent's\b/gi, "your client's")
    .replace(/\byour parent\b/gi, 'your client')
    .replace(/\btheir\b/g, "their")
    .replace(/\byour family\b/gi, 'the care team');
}

const PERSONA_OPTIONS: { value: Persona; label: string; description: string; icon: string }[] = [
  { value: 'self', label: "I'm checking for myself", description: 'I want to see how safe and set up my own home is.', icon: '🏠' },
  { value: 'family', label: "I'm checking for a loved one", description: "I'm a family member looking out for a parent or someone I care about.", icon: '💙' },
  { value: 'pro', label: "I'm a caregiver or professional", description: "I'm evaluating a client's home setup.", icon: '🩺' },
];

const HomeSafetyQuiz = () => {
  const [phase, setPhase] = useState<Phase>('intro');
  const [persona, setPersona] = useState<Persona>('family');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [unknowns, setUnknowns] = useState<Record<string, boolean>>({});
  // Track selected option by index (not points) to avoid highlight bugs
  // when two options share the same point value.
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [copied, setCopied] = useState(false);

  const currentQ = QUESTIONS[step];
  const totalScore = Object.values(answers).reduce((s, v) => s + v, 0);
  const overallPct = Math.round((totalScore / MAX_SCORE) * 100);
  const band = overallBand(totalScore);

  const selectOption = (idx: number, isUnknown?: boolean) => {
    setSelectedIdx(idx);
    setUnknowns((prev) => ({ ...prev, [currentQ.id]: !!isUnknown }));
  };

  const confirmAndAdvance = () => {
    if (selectedIdx === null) return;
    const points = currentQ.options[selectedIdx].points;
    setAnswers((prev) => ({ ...prev, [currentQ.id]: points }));
    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
      // Restore selection if user already answered next question (back then forward)
      const nextQ = QUESTIONS[step + 1];
      const prevAnswer = answers[nextQ.id];
      if (prevAnswer !== undefined) {
        const prevIdx = nextQ.options.findIndex((o) => o.points === prevAnswer);
        setSelectedIdx(prevIdx >= 0 ? prevIdx : null);
      } else {
        setSelectedIdx(null);
      }
    } else {
      setPhase('email');
    }
  };

  const goBack = () => {
    if (step > 0) {
      const prevQ = QUESTIONS[step - 1];
      const prevAnswer = answers[prevQ.id];
      if (prevAnswer !== undefined) {
        const prevIdx = prevQ.options.findIndex((o) => o.points === prevAnswer);
        setSelectedIdx(prevIdx >= 0 ? prevIdx : null);
      } else {
        setSelectedIdx(null);
      }
      setStep((s) => s - 1);
    } else {
      setPhase('persona');
    }
  };

  const submitEmail = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const pillarScores = {
        safety: pillarScore(answers, 'safety'),
        wellness: pillarScore(answers, 'wellness'),
        connection: pillarScore(answers, 'connection'),
      };
      // Determine weakest pillar for Dreamlit email 2 variant
      const weakest = (['safety', 'wellness', 'connection'] as const).reduce(
        (lowest, key) => (pillarScores[key].pct < pillarScores[lowest].pct ? key : lowest),
        'safety' as 'safety' | 'wellness' | 'connection',
      );
      const params = new URLSearchParams(window.location.search);
      await supabase.from('quiz_leads' as any).insert({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim() || null,
        quiz_type: 'home_safety',
        total_score: totalScore,
        max_score: MAX_SCORE,
        band: band.label,
        answers,
        pillar_scores: pillarScores,
        weakest_pillar: weakest,
        drip_status: 'pending',
        drip_next_send_at: new Date().toISOString(),
        source_url: window.location.href,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
      });
    } catch {
      // non-blocking: still show results even if save fails
    }
    setSubmitting(false);
    setPhase('results');
  };

  const skipEmail = () => setPhase('results');

  const shareResults = async () => {
    const shareText =
      `I just took SeniorThrive's Home Safety Check-In and scored "${band.label}." ` +
      `Take it too so we can compare notes and talk about what to do next: ${QUIZ_URL}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Home Safety Check-In', text: shareText });
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard unavailable, no-op
    }
  };

  /* ---- Render phases ---- */

  // INTRO
  if (phase === 'intro') {
    return (
      <>
        <SEOHead />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
          <QuizHeader />
          <main className="flex-1 flex items-center justify-center px-4 py-12">
            <div className="max-w-2xl w-full text-center">
              <div className="inline-flex items-center gap-2 text-blue-700 px-4 py-2 rounded-full text-sm font-bold mb-6">
                <HomeIcon className="w-4 h-4" />
                Free 2-Minute Check-In
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
                How Safe Is Your Home Setup?
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-xl mx-auto">
                Answer 7 quick questions and get a personalized Home Safety
                score with clear recommendations, so you know where to
                focus first.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Button
                  size="lg"
                  className="text-lg px-10 py-6 rounded-xl font-bold"
                  onClick={() => setPhase('persona')}
                >
                  Start the Quiz
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" /> 7
                  questions
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" /> Takes
                  under 2 minutes
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" /> 100%
                  free, no account needed
                </span>
              </div>
            </div>
          </main>
          {/* SEO content: crawlable text for search engine discovery */}
          <section className="px-4 py-16 bg-white">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                What This Quiz Covers
              </h2>
              <div className="grid sm:grid-cols-3 gap-6 mb-12">
                <div className="text-center">
                  <ShieldCheckIcon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Home Safety</h3>
                  <p className="text-sm text-slate-600">
                    Grab bars, emergency access, fall history, and physical home modifications that reduce risk.
                  </p>
                </div>
                <div className="text-center">
                  <HeartIcon className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Daily Wellness</h3>
                  <p className="text-sm text-slate-600">
                    Medication management, daily routines, nutrition habits, and overall health awareness.
                  </p>
                </div>
                <div className="text-center">
                  <UserGroupIcon className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Family Connection</h3>
                  <p className="text-sm text-slate-600">
                    Social engagement, family communication, isolation risk, and support network strength.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4 text-left">
                <details className="border rounded-lg p-4">
                  <summary className="font-bold text-slate-900 cursor-pointer">
                    Who is this quiz for?
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">
                    Anyone who wants to check how safe and supportive a home setup is for an older adult. Adult children checking on a parent, seniors evaluating their own home, and professional caregivers assessing a client can all use it.
                  </p>
                </details>
                <details className="border rounded-lg p-4">
                  <summary className="font-bold text-slate-900 cursor-pointer">
                    How long does the quiz take?
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">
                    Under 2 minutes. There are 7 multiple-choice questions covering home safety, daily wellness, and family connection. You get results instantly.
                  </p>
                </details>
                <details className="border rounded-lg p-4">
                  <summary className="font-bold text-slate-900 cursor-pointer">
                    Do I need to create an account?
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">
                    No. The quiz is completely free and does not require an account. You can optionally enter your email to save your results and get personalized recommendations.
                  </p>
                </details>
                <details className="border rounded-lg p-4">
                  <summary className="font-bold text-slate-900 cursor-pointer">
                    What happens after I finish the quiz?
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">
                    You receive a personalized Home Safety Score across three pillars: Safety, Wellness, and Connection. Each pillar includes specific, actionable recommendations based on your answers.
                  </p>
                </details>
                <details className="border rounded-lg p-4">
                  <summary className="font-bold text-slate-900 cursor-pointer">
                    Is my information private?
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">
                    Yes. Your quiz answers are processed in your browser and are not stored unless you choose to save your results. Read our privacy policy for full details.
                  </p>
                </details>
              </div>
            </div>
          </section>
          <QuizFooter />
        </div>
      </>
    );
  }

  // PERSONA SELECTION
  if (phase === 'persona') {
    return (
      <>
        <SEOHead />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
          <QuizHeader />
          <main className="flex-1 flex items-center justify-center px-4 py-12">
            <div className="max-w-2xl w-full">
              <div className="mb-8">
                <button
                  onClick={() => setPhase('intro')}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" /> Back
                </button>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 text-center">
                Who are you checking for?
              </h2>
              <p className="text-lg text-slate-600 mb-8 text-center">
                This helps us tailor the questions and recommendations.
              </p>
              <div className="space-y-3">
                {PERSONA_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setPersona(opt.value);
                      setPhase('quiz');
                    }}
                    className="w-full text-left px-6 py-5 rounded-xl border-2 border-slate-200 bg-white hover:border-primary hover:shadow-raised transition-all flex items-start gap-4"
                  >
                    <span className="text-2xl mt-0.5">{opt.icon}</span>
                    <div>
                      <span className="text-lg text-slate-800 font-bold block">{opt.label}</span>
                      <span className="text-sm text-slate-500">{opt.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </main>
          <QuizFooter />
        </div>
      </>
    );
  }

  // QUIZ STEP
  if (phase === 'quiz') {
    const progress = ((step + 1) / QUESTIONS.length) * 100;

    return (
      <>
        <SEOHead />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
          <QuizHeader />
          <main className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="max-w-2xl w-full">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-slate-500 mb-2">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-1 hover:text-slate-700 transition-colors"
                  >
                    <ArrowLeftIcon className="w-4 h-4" /> Back
                  </button>
                  <span>
                    Question {step + 1} of {QUESTIONS.length}
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Question */}
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
                {personalize(currentQ.question, persona)}
              </h2>

              {/* Options, select only, no auto-advance */}
              <div className="space-y-3">
                {currentQ.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => selectOption(i, opt.isUnknown)}
                    className={`w-full text-left px-6 py-5 rounded-xl border-2 transition-all duration-200
                      ${
                        selectedIdx === i
                          ? 'border-primary'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-raised'
                      }`}
                  >
                    <span className="text-lg text-slate-800 font-medium">
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Explicit Next button, intentional advancement */}
              <div className="mt-6 flex justify-end">
                <Button
                  size="lg"
                  className="text-lg px-8 py-5 rounded-xl font-bold"
                  disabled={selectedIdx === null}
                  onClick={confirmAndAdvance}
                >
                  {step < QUESTIONS.length - 1 ? 'Next' : 'See My Results'}
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </main>
          <QuizFooter />
        </div>
      </>
    );
  }

  // EMAIL CAPTURE
  if (phase === 'email') {
    return (
      <>
        <SEOHead />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
          <QuizHeader />
          <main className="flex-1 flex items-center justify-center px-4 py-12">
            <div className="max-w-lg w-full">
              <div className="bg-white rounded-md shadow-lg border border-slate-100 p-8 sm:p-10">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4">
                    <CheckCircleIcon className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Your results are ready!
                  </h2>
                  <p className="text-slate-600">
                    Enter your email and we'll save your Home Safety results
                    so you can revisit them and share with family.
                  </p>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!email.trim()) return;
                    submitEmail();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="quiz-first-name"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      First name{' '}
                      <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      id="quiz-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="e.g. Sarah"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-lg"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="quiz-email"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Email address
                    </label>
                    <input
                      id="quiz-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-lg"
                    />
                  </div>
                  {submitError && (
                    <p className="text-red-600 text-sm">{submitError}</p>
                  )}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg py-6 rounded-xl font-bold"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'See My Results'}
                  </Button>
                </form>

                <button
                  onClick={skipEmail}
                  className="block mx-auto mt-4 text-base text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Skip and see results
                </button>

                <p className="text-xs text-slate-400 text-center mt-4">
                  We will never share your email. Unsubscribe anytime.
                </p>
              </div>
            </div>
          </main>
          <QuizFooter />
        </div>
      </>
    );
  }

  // RESULTS
  const safety = pillarScore(answers, 'safety');
  const wellness = pillarScore(answers, 'wellness');
  const connection = pillarScore(answers, 'connection');
  const pillars = [
    { key: 'safety' as const, ...safety },
    { key: 'wellness' as const, ...wellness },
    { key: 'connection' as const, ...connection },
  ];

  // Gather which questions the user said "I don't know" on
  const unknownQuestions = QUESTIONS.filter((q) => unknowns[q.id]);

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
        <QuizHeader />
        <main className="flex-1 px-4 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Overall score card, band label primary, percentage for consistency */}
            <div
              className={`${band.bg} ${band.border} border rounded-md p-8 sm:p-10 text-center mb-10`}
            >
              <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
                Your Home Safety Score
              </p>
              <p className={`text-3xl sm:text-4xl font-bold ${band.color} mb-2`}>
                {band.label}
              </p>
              <p className="text-lg text-slate-500 mb-4">{overallPct}%</p>
              <div className="w-full max-w-xs mx-auto h-3 bg-white/60 rounded-full overflow-hidden mb-5">
                <div
                  className="h-full bg-current rounded-full transition-all duration-700"
                  style={{ width: `${overallPct}%`, color: band.color === 'text-emerald-600' ? '#059669' : band.color === 'text-amber-600' ? '#d97706' : band.color === 'text-orange-600' ? '#ea580c' : '#dc2626' }}
                />
              </div>
              <p className="text-slate-700 text-lg leading-relaxed max-w-xl mx-auto">
                {band.message}
              </p>
            </div>

            {/* Share with family */}
            <div className="flex justify-center mb-10">
              <button
                onClick={shareResults}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:border-slate-300 hover:shadow-raised transition-all"
              >
                {copied ? (
                  <>
                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-emerald-500" />
                    Copied! Send it to your family
                  </>
                ) : (
                  <>
                    <ShareIcon className="w-5 h-5" />
                    Share with a family member
                  </>
                )}
              </button>
            </div>

            {/* Pillar breakdown, percentages to match top card */}
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              Score Breakdown
            </h3>
            <div className="grid gap-4 mb-10">
              {pillars.map((p) => {
                const meta = PILLAR_META[p.key];
                return (
                  <div
                    key={p.key}
                    className="bg-white rounded-xl border border-slate-100 p-5 flex items-center gap-5"
                  >
                    <div
                      className={`w-12 h-12 ${meta.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <meta.Icon className={`w-6 h-6 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="font-bold text-slate-800">
                          {meta.label}
                        </span>
                        <span className="text-sm text-slate-500">
                          {p.pct}%
                        </span>
                      </div>
                      <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${meta.barColor} rounded-full transition-all duration-700`}
                          style={{ width: `${p.pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* "Areas to learn more about", surfaced when user had unknowns */}
            {unknownQuestions.length > 0 && (
              <div className="bg-blue-50 rounded-md border border-blue-100 p-8 mb-8">
                <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <MagnifyingGlassIcon className="w-5 h-5 text-blue-500" />
                  Areas to Learn More About
                </h3>
                <p className="text-slate-600 mb-4">
                  You weren't sure about {unknownQuestions.length === 1 ? 'one question' : `${unknownQuestions.length} questions`}. That's not a problem, it just means these are good topics to explore{persona === 'self' ? ' with your doctor' : persona === 'pro' ? ' with the care team' : ' with your parent or their doctor'}.
                </p>
                <ul className="space-y-2">
                  {unknownQuestions.map((q) => (
                    <li key={q.id} className="flex items-start gap-3">
                      <MagnifyingGlassIcon className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                      <span className="text-slate-700">{q.question}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            <div className="bg-white rounded-md border border-slate-100 p-8 mb-10">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <LightBulbIcon className="w-5 h-5 text-amber-500" />
                Recommended Next Steps
              </h3>
              <ul className="space-y-3">
                {safety.pct < 80 && (
                  <RecommendationItem text={personalize("Walk through every room and check for trip hazards, loose rugs, and poor lighting.", persona)} />
                )}
                {safety.pct < 50 && (
                  <RecommendationItem text="Install grab bars in the bathroom and handrails on all stairs as soon as possible." />
                )}
                {answers.emergency_access !== undefined &&
                  answers.emergency_access < 3 && (
                    <RecommendationItem text={personalize("Set up a reliable way for your parent to call for help from any room (medical alert, smart speaker, or phone in every room).", persona)} />
                  )}
                {wellness.pct < 80 && (
                  <RecommendationItem text="Create a simple medication checklist or use a pill organizer to build daily consistency." />
                )}
                {answers.routine !== undefined && answers.routine < 2 && (
                  <RecommendationItem text={personalize("Help establish steady routines (morning coffee, afternoon walk, evening check-in) to create predictability.", persona)} />
                )}
                {connection.pct < 80 && (
                  <RecommendationItem text={personalize("Set up a recurring family check-in, even a quick daily text or photo share, to stay connected without being intrusive.", persona)} />
                )}
                {answers.awareness !== undefined && answers.awareness < 2 && (
                  <RecommendationItem text={personalize("A shared system can help your family know when daily routines happen: not because of worry, but because it replaces worry with confidence.", persona)} />
                )}
              </ul>
            </div>

            {/* CTA, framed as an upgrade, not a paywall */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/15 rounded-md p-8 sm:p-10 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                Want to stay on top of this?
              </h3>
              <p className="text-slate-600 text-lg mb-6 max-w-xl mx-auto">
                SeniorThrive gives your family a shared, real-time view of how
                things are going at home. Free to start, no credit card
                required. Setup takes about 3 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="text-lg px-10 py-6 rounded-xl font-bold"
                  asChild
                >
                  <a
                    href="https://app.seniorthrive.com/auth?signup=true&utm_source=quiz&utm_medium=home_safety_quiz&utm_campaign=quiz_results"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Create Free Account
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-10 py-6 rounded-xl font-bold"
                  asChild
                >
                  <Link to="/thrivefamily">Learn About the Family Plan</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <QuizFooter />
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared sub-components                                              */
/* ------------------------------------------------------------------ */

function SEOHead() {
  return (
    <EnhancedSEO
      title="Free Home Safety Quiz for Seniors & Families"
      description="Answer 7 quick questions and get a personalized Home Safety score with clear recommendations. Free, no account needed, takes under 2 minutes."
      canonical="https://seniorthrive.com/home-safety-quiz"
      faq={[
        { question: 'Who is this quiz for?', answer: 'Anyone who wants to check how safe and supportive a home setup is for an older adult. Adult children checking on a parent, seniors evaluating their own home, and professional caregivers assessing a client can all use it.' },
        { question: 'How long does the quiz take?', answer: 'Under 2 minutes. There are 7 multiple-choice questions covering home safety, daily wellness, and family connection. You get results instantly.' },
        { question: 'Do I need to create an account?', answer: 'No. The quiz is completely free and does not require an account. You can optionally enter your email to save your results and get personalized recommendations.' },
        { question: 'What happens after I finish the quiz?', answer: 'You receive a personalized Home Safety Score across three pillars: Safety, Wellness, and Connection. Each pillar includes specific, actionable recommendations based on your answers.' },
        { question: 'Is my information private?', answer: 'Yes. Your quiz answers are processed in your browser and are not stored unless you choose to save your results.' },
      ]}
    />
  );
}

function QuizHeader() {
  return (
    <header className="py-6 px-4">
      <div className="flex justify-center">
        <Link to="/">
          <img
            src="/uploads/hero-professional-success-wc.jpg"
            alt="SeniorThrive"
            className="h-10 md:h-12"
          />
        </Link>
      </div>
    </header>
  );
}

function QuizFooter() {
  return (
    <footer className="py-6 px-4">
      <div className="text-center">
        <p className="text-sm text-slate-500 mb-2">
          &copy; 2026 SeniorThrive. All rights reserved.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/privacy"
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
}

function RecommendationItem({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
      <span className="text-slate-700">{text}</span>
    </li>
  );
}

export default HomeSafetyQuiz;
